import { ArrowRightLeft, Sparkles, Undo2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { formatError, type UiValues } from './_shared';
import { dispatchOpenSuggest } from './calc-assist-events';
import { type CalcParseResult, useParseCalc } from './use-parse-calc';

// Candidate pool sizes — must stay in sync with the CANDIDATES arrays
// in rest-api/src/routes/calc-suggest.ts and calc-recovery.ts. If a
// future session adds or removes blanks, update here too.
const TEE_COUNT = 8;
const HOODIE_COUNT = 3;
const ALL_COUNT = TEE_COUNT + HOODIE_COUNT;

function suggestionSubtitle(category: 'tee' | 'hoodie' | undefined): string {
  if (category === 'tee') return `Prices ${TEE_COUNT} tee blanks for this job.`;
  if (category === 'hoodie') return `Prices ${HOODIE_COUNT} hoodie blanks for this job.`;
  return `Prices ${ALL_COUNT} blanks for this job.`;
}

/**
 * Natural-language entry for the calculator. Paste or type a sentence
 * describing the quote; a Haiku-backed parser returns the fields it
 * recognised and we merge them into the form.
 *
 * Guardrails:
 *   - Tool-use only on the backend (no free-form prose).
 *   - Never overwrites fields the LLM didn't mention — merge-over, not
 *     replace.
 *   - One-click Undo restores the pre-parse values.
 *   - Cmd/Ctrl-K focuses the textarea; Cmd/Ctrl-Enter submits.
 */
export function ParseTextbox({ form }: { form: UseFormReturn<UiValues> }) {
  const [text, setText] = useState('');
  const [previous, setPrevious] = useState<UiValues | null>(null);
  const [notes, setNotes] = useState<string | null>(null);
  const [wantsSuggestion, setWantsSuggestion] = useState(false);
  const [category, setCategory] = useState<'tee' | 'hoodie' | undefined>(undefined);
  const [colorHint, setColorHint] = useState<string | null>(null);
  const { state, parse, reset } = useParseCalc();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Cmd/Ctrl-K focuses. Global shortcut so the owner can hit it from any
  // field in the form.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        textareaRef.current?.focus();
        textareaRef.current?.select();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const applyResult = useCallback(
    (result: CalcParseResult) => {
      const snapshot = form.getValues();
      setPrevious(snapshot);
      setNotes(result.notes ?? null);
      setWantsSuggestion(result.wantsGarmentSuggestion === true);
      setCategory(result.garmentCategory);
      // When the parse captured a color but no SS style, the SS color
      // combobox can't show it (it only lists colors for a picked style).
      // Surface the preferred color as a chip so the owner sees the value is
      // remembered — the suggest pane consumes it via form.getValues().
      const parsedStyle = typeof result.style === 'string' ? result.style.trim() : '';
      setColorHint(result.colorName && parsedStyle.length === 0 ? result.colorName : null);
      // Merge: only set fields the LLM returned. Everything else stays.
      // Meta keys (notes / flags / category) are surfaced separately;
      // they'd fail RHF if fed through setValue.
      const META_KEYS = new Set(['notes', 'wantsGarmentSuggestion', 'garmentCategory']);
      for (const [key, value] of Object.entries(result) as [keyof CalcParseResult, unknown][]) {
        if (META_KEYS.has(key) || value === undefined) continue;
        form.setValue(key as keyof UiValues, value as never, {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
    },
    [form],
  );

  async function handleSubmit() {
    const trimmed = text.trim();
    if (trimmed.length === 0) return;
    const result = await parse(trimmed);
    if (result) applyResult(result);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      void handleSubmit();
    }
  }

  function handleUndo() {
    if (!previous) return;
    form.reset(previous, { keepDefaultValues: true });
    setPrevious(null);
    setNotes(null);
    setWantsSuggestion(false);
    setCategory(undefined);
    setColorHint(null);
    reset();
  }

  function handleOpenSuggest() {
    dispatchOpenSuggest({ category });
  }

  const loading = state.status === 'loading';
  const errorTitle = state.status === 'error' ? formatError(state.error).title : null;
  const errorDetail = state.status === 'error' ? formatError(state.error).message : null;

  return (
    <section className="relative space-y-2 overflow-hidden rounded-lg border border-primary/25 bg-card p-3 shadow-sm before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:rounded-l-lg before:bg-primary before:content-['']">
      <div className="flex items-baseline justify-between">
        <label
          htmlFor="parse-textbox"
          className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground"
        >
          <Sparkles className="size-3.5" />
          Describe the job
        </label>
        <span className="text-[10px] text-muted-foreground">
          <kbd className="rounded border bg-background px-1 py-0.5 font-mono text-[9px]">⌘K</kbd>{' '}
          focus ·{' '}
          <kbd className="rounded border bg-background px-1 py-0.5 font-mono text-[9px]">⌘↵</kbd>{' '}
          parse
        </span>
      </div>
      <textarea
        ref={textareaRef}
        id="parse-textbox"
        className="w-full rounded-md border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
        rows={2}
        placeholder="e.g. 72 gildan 5000 black, 2c front 1c back, B2B, 10% off"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        maxLength={500}
        disabled={loading}
      />
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1 text-xs text-muted-foreground">
          {notes && state.status === 'success' && (
            <p className="truncate" title={notes}>
              {notes}
            </p>
          )}
          {errorTitle && (
            <p className="truncate text-destructive" title={errorDetail ?? undefined}>
              {errorTitle}: {errorDetail}
            </p>
          )}
          {!notes && !errorTitle && (
            <p className="text-muted-foreground/70">
              Fills sizes, locations, garment, mode, and discount. Never overwrites fields you
              didn't mention.
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {previous && !loading && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleUndo}
              className="gap-1.5 text-muted-foreground"
            >
              <Undo2 className="size-3.5" />
              Undo
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            onClick={handleSubmit}
            disabled={loading || text.trim().length === 0}
          >
            {loading ? 'Parsing…' : 'Parse'}
          </Button>
        </div>
      </div>
      {colorHint && state.status === 'success' && (
        <p className="text-[11px] text-muted-foreground">
          Preferred color: <span className="font-medium text-foreground">{colorHint}</span> · pick a
          style to apply it, or ask AI for a cheapest blank below.
        </p>
      )}
      {wantsSuggestion && state.status === 'success' && (
        <button
          type="button"
          onClick={handleOpenSuggest}
          className="flex w-full items-center justify-between gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-left text-sm transition-colors hover:bg-primary/10"
        >
          <span className="flex items-center gap-2">
            <ArrowRightLeft className="size-3.5 text-primary" />
            <span>
              <span className="font-medium">Find the cheapest garment</span>
              <span className="ml-2 text-xs text-muted-foreground">
                {suggestionSubtitle(category)}
              </span>
            </span>
          </span>
          <span className="text-xs text-primary">Open →</span>
        </button>
      )}
    </section>
  );
}
